/**
 * Shared today-position utility for dashboard views.
 *
 * Every date-aware view needs the same three pieces of "now" derived from the
 * current view window and the workspace timezone:
 *
 *   - `todayMs`         — millisecond timestamp for the start of "today"
 *                         in the workspace's timezone (falls back to the local
 *                         zone when none is set).
 *   - `todayRatio`      — 0..1 position of `todayMs` inside the view window,
 *                         or null when today is outside the window (so views
 *                         can hide the line/sweep instead of clamping it to
 *                         an edge).
 *   - `isTodayInWindow` — convenience boolean for the same check.
 *
 * The util is pure so it composes naturally with `useMemo` and with the pure
 * `buildViewProjectionSnapshot` in `viewProjection.ts`.
 */

export interface TodayContext {
  readonly todayMs: number
  readonly todayRatio: number | null
  readonly isTodayInWindow: boolean
  readonly viewStartMs: number
  readonly viewEndMs: number
  readonly timezone: string | undefined
}

const startOfTodayMs = (now: Date, timezone: string | undefined): number => {
  // Intl date-parts in the workspace timezone → build a UTC epoch representing
  // "start of that day, in that zone." Cheap enough to recompute per render;
  // the projection layer memoizes it anyway.
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = Object.fromEntries(
    formatter.formatToParts(now).map((part) => [part.type, part.value]),
  )
  const year = Number(parts.year)
  const month = Number(parts.month)
  const day = Number(parts.day)
  const hour = Number(parts.hour ?? '0')
  const minute = Number(parts.minute ?? '0')
  const second = Number(parts.second ?? '0')

  // `now` in the target zone is (Y,M,D,H,M,S). The offset between UTC and that
  // zone at `now` is `now.getTime() - Date.UTC(Y,M,D,H,M,S)`. Apply that same
  // offset to the zone's start-of-day to get the correct UTC epoch.
  const zoneAsUtc = Date.UTC(year, month - 1, day, hour, minute, second)
  const zoneOffsetMs = now.getTime() - zoneAsUtc
  const startOfDayZoneAsUtc = Date.UTC(year, month - 1, day, 0, 0, 0)
  return startOfDayZoneAsUtc + zoneOffsetMs
}

export const computeTodayContext = (
  viewStartMs: number,
  viewEndMs: number,
  input: {
    timezone?: string
    now?: Date
  } = {},
): TodayContext => {
  const now = input.now ?? new Date()
  const todayMs = startOfTodayMs(now, input.timezone)
  const duration = Math.max(1, viewEndMs - viewStartMs)
  const isInWindow = todayMs >= viewStartMs && todayMs <= viewEndMs
  return {
    todayMs,
    todayRatio: isInWindow ? (todayMs - viewStartMs) / duration : null,
    isTodayInWindow: isInWindow,
    viewStartMs,
    viewEndMs,
    timezone: input.timezone,
  }
}
